// Hook para gerenciar salvamento da apreciação
import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '@/services/api';
import { useI18n } from '@/contexts/i18n-hook';

// Função auxiliar para converter URL completa em URL relativa
const converterParaUrlRelativa = (url: string | null | undefined): string | null => {
  if (!url) return null;
  
  // Se já é uma URL relativa (começa com /), retornar como está
  if (url.startsWith('/')) {
    return url;
  }
  
  // Se é uma URL completa, extrair apenas a parte relativa
  try {
    const urlObj = new URL(url);
    return urlObj.pathname;
  } catch {
    // Se não conseguir fazer parse, retornar como está
    return url;
  }
};

interface UseApreciacaoSaveProps {
  apreciacaoId: string | undefined;
  dados: any;
  riscos: any[];
  renovarLock?: () => Promise<boolean>;
  bloqueadoPorOutro?: () => boolean;
  erroLock?: string | null;
  onSalvo?: () => void | Promise<void>;
}

export function useApreciacaoSave({
  apreciacaoId,
  dados,
  riscos,
  renovarLock,
  bloqueadoPorOutro,
  erroLock,
  onSalvo,
}: UseApreciacaoSaveProps) {
  const { t } = useI18n();
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const salvar = useCallback(async (): Promise<boolean> => {
    if (!apreciacaoId || !dados) return false;

    // Verificar se está bloqueado por outro usuário
    if (erroLock || bloqueadoPorOutro?.()) {
      alert(t('apreciacoes.salvar.bloqueada'));
      setTimeout(() => {
        navigate('/apreciacoes');
      }, 2000);
      return false;
    }

    // Renovar lock ao salvar
    if (renovarLock) {
      await renovarLock();
    }

    setSaving(true);
    try {
      // Converter dataEmissao para formato ISO se existir
      let dataEmissaoFormatada = dados.dataEmissao;
      if (dataEmissaoFormatada && dataEmissaoFormatada.trim() !== '') {
        const data = new Date(dataEmissaoFormatada);
        if (!isNaN(data.getTime())) {
          dataEmissaoFormatada = data.toISOString();
        } else {
          dataEmissaoFormatada = undefined;
        }
      } else {
        dataEmissaoFormatada = undefined;
      }

      // Preparar dados de atualização
      const dadosAtualizacao: any = {};

      // Campos obrigatórios
      if (dados.numero !== undefined) dadosAtualizacao.numero = dados.numero;
      if (dados.titulo !== undefined) {
        dadosAtualizacao.titulo = dados.titulo;
        dadosAtualizacao.descricao = dados.titulo;
      }
      if (dados.statusLiberacao !== undefined) dadosAtualizacao.statusLiberacao = dados.statusLiberacao;

      // Função auxiliar para campos opcionais que o usuário pode limpar.
      // Campo vazio vira null (coluna String? no Prisma) em vez de ser omitido do
      // payload — omitir faria o backend manter o valor antigo e o campo "voltaria"
      // depois do recarregamento.
      const adicionarPermitindoLimpar = (campo: string, valor: any) => {
        if (valor === undefined) return;
        if (valor === null) {
          dadosAtualizacao[campo] = null;
        } else if (typeof valor === 'string') {
          dadosAtualizacao[campo] = valor.trim() === '' ? null : valor;
        } else {
          dadosAtualizacao[campo] = valor;
        }
      };

      // Campos opcionais do cabeçalho/identificação documental
      adicionarPermitindoLimpar('numeroEmitente', dados.numeroEmitente);
      adicionarPermitindoLimpar('emitirCREA', dados.emitirCREA);
      adicionarPermitindoLimpar('numeroArcelorMittal', dados.numeroArcelorMittal);
      adicionarPermitindoLimpar('codigoInterno', dados.codigoInterno);
      // emitente é NOT NULL no banco: limpar significa string vazia, nunca null
      if (dados.emitente !== undefined) {
        dadosAtualizacao.emitente = dados.emitente ?? '';
      }
      // numeroDocumento e nomeMaquina devem ser sempre enviados, mesmo se vazios, para permitir limpar valores existentes
      if (dados.numeroDocumento !== undefined) {
        dadosAtualizacao.numeroDocumento = dados.numeroDocumento || null;
      }
      if (dados.demanda !== undefined) {
        dadosAtualizacao.demanda = dados.demanda || null;
      }
      
      // Campos de identificação documental da máquina
      if (dados.revisao !== undefined) {
        dadosAtualizacao.revisao = dados.revisao;
      }
      // nomeMaquina deve ser sempre enviado, mesmo se vazio, para permitir limpar valores existentes
      if (dados.nomeMaquina !== undefined) {
        dadosAtualizacao.nomeMaquina = dados.nomeMaquina || null;
      }
      adicionarPermitindoLimpar('tipoMaquina', dados.tipoMaquina);
      adicionarPermitindoLimpar('identificacaoMaquina', dados.identificacaoMaquina);
      adicionarPermitindoLimpar('setorMaquina', dados.setorMaquina);
      adicionarPermitindoLimpar('funcaoMaquina', dados.funcaoMaquina);
      
      if (dados.ativoId !== undefined) {
        dadosAtualizacao.ativoId = dados.ativoId || null;
      }
      
      if (dataEmissaoFormatada) dadosAtualizacao.dataEmissao = dataEmissaoFormatada;
      
      // Campos de data de elaboração, verificação e aprovação
      if (dados.dataElaboracao) {
        dadosAtualizacao.dataElaboracao = new Date(dados.dataElaboracao);
      } else if (dados.dataElaboracao === '') {
        dadosAtualizacao.dataElaboracao = null;
      }
      if (dados.dataVerificacao) {
        dadosAtualizacao.dataVerificacao = new Date(dados.dataVerificacao);
      } else if (dados.dataVerificacao === '') {
        dadosAtualizacao.dataVerificacao = null;
      }
      if (dados.dataAprovacao) {
        dadosAtualizacao.dataAprovacao = new Date(dados.dataAprovacao);
      } else if (dados.dataAprovacao === '') {
        dadosAtualizacao.dataAprovacao = null;
      }
      
      // Campos de limites e informações
      adicionarPermitindoLimpar('limitesUso', dados.limitesUso);
      adicionarPermitindoLimpar('limitesEspaco', dados.limitesEspaco);
      adicionarPermitindoLimpar('limitesAdicionais', dados.limitesAdicionais);
      adicionarPermitindoLimpar('limitesAmbientais', dados.limitesAmbientais);
      adicionarPermitindoLimpar('limitacoes', dados.limitacoes);

      // Listas: enviar mesmo quando vazias, para permitir remover todos os itens
      if (Array.isArray(dados.mauUsoRazoavelmentePrevisivel)) {
        dadosAtualizacao.mauUsoRazoavelmentePrevisivel = dados.mauUsoRazoavelmentePrevisivel;
      }
      if (Array.isArray(dados.normasTecnicasReferencia)) {
        dadosAtualizacao.normasTecnicasReferencia = dados.normasTecnicasReferencia;
      }

      adicionarPermitindoLimpar('nota1', dados.nota1);
      adicionarPermitindoLimpar('nota2', dados.nota2);
      adicionarPermitindoLimpar('operadorEntrevistado', dados.operadorEntrevistado);
      adicionarPermitindoLimpar('cargoOperador', dados.cargoOperador);
      adicionarPermitindoLimpar('experiencia', dados.experiencia);
      adicionarPermitindoLimpar('turnosTrabalho', dados.turnosTrabalho);
      adicionarPermitindoLimpar('frequenciaUso', dados.frequenciaUso);
      adicionarPermitindoLimpar('frequenciaAcessoOperacao', dados.frequenciaAcessoOperacao);
      adicionarPermitindoLimpar('frequenciaAcessoManutencao', dados.frequenciaAcessoManutencao);
      adicionarPermitindoLimpar('modoOperacao', dados.modoOperacao);
      adicionarPermitindoLimpar('historicoAcidentes', dados.historicoAcidentes);
      adicionarPermitindoLimpar('observacoesEntrevista', dados.observacoesEntrevista);
      
      // Processar dataEntrevista: converter string para Date ou manter undefined se vazio
      if (dados.dataEntrevista && dados.dataEntrevista.trim() !== '') {
        try {
          const dataEntrevistaDate = new Date(dados.dataEntrevista);
          if (!isNaN(dataEntrevistaDate.getTime())) {
            dadosAtualizacao.dataEntrevista = dataEntrevistaDate;
          }
        } catch (error) {
          console.warn('Erro ao processar dataEntrevista:', error);
        }
      } else if (dados.dataEntrevista === '' || dados.dataEntrevista === null) {
        // Permitir limpar a data se for enviada como string vazia ou null
        dadosAtualizacao.dataEntrevista = null;
      }
      
      // Campos técnicos
      adicionarPermitindoLimpar('comprimentoHorizontal', dados.comprimentoHorizontal);
      adicionarPermitindoLimpar('elevacao', dados.elevacao);
      adicionarPermitindoLimpar('capacidadeCorreia', dados.capacidadeCorreia);
      adicionarPermitindoLimpar('larguraCorreia', dados.larguraCorreia);
      adicionarPermitindoLimpar('velocidadeCorreia', dados.velocidadeCorreia);
      adicionarPermitindoLimpar('material', dados.material);
      adicionarPermitindoLimpar('contraRecuo', dados.contraRecuo);
      adicionarPermitindoLimpar('freio', dados.freio);
      adicionarPermitindoLimpar('potencia', dados.potencia);
      adicionarPermitindoLimpar('tensaoAcionamento', dados.tensaoAcionamento);
      adicionarPermitindoLimpar('tensaoComando', dados.tensaoComando);

      // Campos de espaços disponíveis (booleanos)
      if (dados.espacoPecasFerramentas !== undefined) {
        dadosAtualizacao.espacoPecasFerramentas = dados.espacoPecasFerramentas;
      }
      if (dados.espacoOperacaoNormal !== undefined) {
        dadosAtualizacao.espacoOperacaoNormal = dados.espacoOperacaoNormal;
      }
      if (dados.espacoManutencao !== undefined) {
        dadosAtualizacao.espacoManutencao = dados.espacoManutencao;
      }
      if (dados.espacoAjuste !== undefined) {
        dadosAtualizacao.espacoAjuste = dados.espacoAjuste;
      }
      if (dados.espacoLimpeza !== undefined) {
        dadosAtualizacao.espacoLimpeza = dados.espacoLimpeza;
      }

      // Campos de imagens da máquina
      // Converter URLs completas para relativas antes de salvar
      if (dados.fotoPanoramicaUrl !== undefined) {
        dadosAtualizacao.fotoPanoramicaUrl = converterParaUrlRelativa(dados.fotoPanoramicaUrl);
      }
      if (dados.fotoIdentificacaoUrl !== undefined) {
        dadosAtualizacao.fotoIdentificacaoUrl = converterParaUrlRelativa(dados.fotoIdentificacaoUrl);
      }

      // Incluir dados da máquina para atualização
      // Sempre enviar o objeto maquina, mesmo se vazio, para garantir que campos vazios sejam salvos
      dadosAtualizacao.maquina = {
        site: dados.maquina?.site || null,
        area: dados.maquina?.area || null,
        local: dados.maquina?.local || null,
        fabricante: dados.maquina?.fabricante || null,
        modelo: dados.maquina?.modelo || null,
        nome: dados.maquina?.nome || null,
        descricao: dados.maquina?.descricao || null,
        serie: dados.maquina?.serie || null,
        ano: dados.maquina?.ano || null,
      };

      // Dados de usuários (elaborador, verificador, aprovador, executante, responsável técnico)
      if (dados.elaboradorUserId !== undefined) {
        dadosAtualizacao.elaboradorUserId = dados.elaboradorUserId || null;
      }
      if (dados.elaboradorExterno !== undefined) {
        dadosAtualizacao.elaboradorExterno = dados.elaboradorExterno || null;
      }
      if (dados.verificadorUserId !== undefined) {
        dadosAtualizacao.verificadorUserId = dados.verificadorUserId || null;
      }
      if (dados.verificadorExterno !== undefined) {
        dadosAtualizacao.verificadorExterno = dados.verificadorExterno || null;
      }
      if (dados.aprovadorUserId !== undefined) {
        dadosAtualizacao.aprovadorUserId = dados.aprovadorUserId || null;
      }
      if (dados.aprovadorExterno !== undefined) {
        dadosAtualizacao.aprovadorExterno = dados.aprovadorExterno || null;
      }
      if (dados.executanteUserId !== undefined) {
        dadosAtualizacao.executanteUserId = dados.executanteUserId || null;
      }
      if (dados.executanteExterno !== undefined) {
        dadosAtualizacao.executanteExterno = dados.executanteExterno || null;
      }
      if (dados.responsavelTecnicoUserId !== undefined) {
        dadosAtualizacao.responsavelTecnicoUserId = dados.responsavelTecnicoUserId || null;
      }
      if (dados.responsavelTecnicoExterno !== undefined) {
        dadosAtualizacao.responsavelTecnicoExterno = dados.responsavelTecnicoExterno || null;
      }

      // Lista de verificação administrativa
      if (dados.listaVerificacaoAdministrativa !== undefined) {
        dadosAtualizacao.listaVerificacaoAdministrativa = dados.listaVerificacaoAdministrativa || null;
      }
      if (dados.camposCliente !== undefined) {
        dadosAtualizacao.camposCliente = dados.camposCliente || null;
      }
      if (dados.checklistTecnicoAdmin !== undefined) {
        dadosAtualizacao.checklistTecnicoAdmin = dados.checklistTecnicoAdmin || null;
      }
      if (dados.conclusoesFinais !== undefined) {
        dadosAtualizacao.conclusoesFinais = dados.conclusoesFinais || null;
      }
      if (dados.exclusaoResponsabilidade !== undefined) {
        dadosAtualizacao.exclusaoResponsabilidade = dados.exclusaoResponsabilidade || null;
      }
      if (dados.cidadeAssinatura !== undefined) {
        dadosAtualizacao.cidadeAssinatura = dados.cidadeAssinatura || null;
      }

      // Atualizar apreciação
      const response = await apiService.atualizarApreciacao(apreciacaoId, dadosAtualizacao);

      if (!response.success) {
        alert(
          t('apreciacoes.salvar.erroDetalhe', {
            detalhe: response.message || t('apreciacoes.erroDesconhecido'),
          })
        );
        return false;
      }

      // Salvar apenas riscos novos (sem ID) - riscos existentes são atualizados via modal
      // Falha em um risco não pode virar "salvo com sucesso": as partes que não
      // subiram são acumuladas e informadas no fim, sem interromper as demais.
      const partesNaoSalvas: string[] = [];
      for (const risco of riscos) {
        if (!risco.id) {
          // Risco novo - adicionar
          const dadosRisco: any = {
            parte: risco.parte || '',
            numero: risco.numero || 1,
            tipoPerigo: risco.tipoPerigo || 'mecanico',
            categoria: risco.categoria || '',
            eventoPerigoso: risco.eventoPerigoso || '',
            consequencias: risco.consequencias || '',
            cicloVida: risco.cicloVida || '',
            conformeAtual: risco.conformeAtual ?? false,
            probabilidadeAtual: risco.probabilidadeAtual ?? 1,
            frequenciaAtual: risco.frequenciaAtual ?? 1,
            gravidadeAtual: risco.gravidadeAtual ?? 1,
            exposicaoAtual: risco.exposicaoAtual ?? 1,
            hrnAtual: risco.hrnAtual ?? 1,
            probabilidadeFutura: risco.probabilidadeFuturo,
            frequenciaFutura: risco.frequenciaFuturo,
            gravidadeFutura: risco.gravidadeFuturo,
            exposicaoFutura: risco.exposicaoFuturo,
            hrnFuturo: risco.hrnFuturo,
            adequacoesTecnicas: risco.adequacoesTecnicas,
            medidasControle: risco.medidasControle,
            exigenciasNormativas: risco.exigenciasNormativas,
            adequacoesNormativas: risco.adequacoesNormativas,
          };

          // Remover campos undefined/null/vazios opcionais
          Object.keys(dadosRisco).forEach(key => {
            const value = dadosRisco[key];
            if (value === undefined || value === null || 
                (typeof value === 'string' && value.trim() === '' && 
                 key !== 'parte' && key !== 'cicloVida' && key !== 'eventoPerigoso' && key !== 'consequencias')) {
              delete dadosRisco[key];
            }
          });

          try {
            await apiService.adicionarRisco(apreciacaoId, dadosRisco);
          } catch (error: any) {
            console.error(`Erro ao adicionar risco ${risco.numero}:`, error);
            partesNaoSalvas.push(
              t('apreciacoes.salvar.itemNaoSalvo', {
                parte: risco.parte || t('apreciacoes.salvar.semParte'),
                numero: risco.numero ?? '?',
                erro: error?.message || t('apreciacoes.salvar.erroMinusculo'),
              })
            );
          }
        }
      }

      // Recarregar dados se callback fornecido
      if (onSalvo) {
        await onSalvo();
      }
      
      if (partesNaoSalvas.length > 0) {
        alert(
          t(
            partesNaoSalvas.length === 1
              ? 'apreciacoes.salvar.parcialUma'
              : 'apreciacoes.salvar.parcialVarias',
            { total: partesNaoSalvas.length, partes: partesNaoSalvas.join('\n') }
          )
        );
        return false;
      }

      alert(t('apreciacoes.salvar.sucesso'));
      return true;
    } catch (error: any) {
      console.error('Erro ao salvar apreciação:', error);
      alert(
        t('apreciacoes.salvar.erroDetalhe', {
          detalhe: error.message || t('apreciacoes.erroDesconhecido'),
        })
      );
      return false;
    } finally {
      setSaving(false);
    }
  }, [apreciacaoId, dados, riscos, renovarLock, bloqueadoPorOutro, erroLock, navigate, onSalvo, t]);

  return {
    saving,
    salvar,
  };
}
